import {observer} from 'mobx-react-lite';
import {FC, useCallback, useEffect, useState} from 'react';
import {matchPath, useLocation, useSearchParams} from 'react-router';

import {appStore} from '@/entities/app';
import {userStore} from '@/entities/user';
import BaseModal from '@/shared/components/modals/base/BaseModal';

import CookieConsentInitial from './components/CookieConsentInitial';
import CookieConsentInitialNonBlocking from './components/CookieConsentInitialNonBlocking';
import CookieConsentPreferences from './components/CookieConsentPreferences';
import {MODAL_STATE, QUERY_PARAM_COOKIE_NON_BLOCKING} from './consts';
import {useCookieConsent} from './hooks/useCookieConsent';
import {CookiePreferences} from './types';

const CookieConsentModal: FC = () => {
  const {
    isOpen,
    openMode,
    initialPreferences,
    onDecision,
    onClosePreferencesMode,
  } = useCookieConsent();

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const forcedNonBlocking = searchParams.has(QUERY_PARAM_COOKIE_NON_BLOCKING);
  const shouldWaitForAppInitialization =
    Boolean(matchPath('/app/*', pathname)) && !userStore.loaded;

  const [modalState, setModalState] = useState<keyof typeof MODAL_STATE>(
    MODAL_STATE.initial
  );

  const resetState = useCallback(() => {
    setModalState(MODAL_STATE.initial);
  }, []);

  const handleClose = useCallback(() => {
    if (openMode === 'preferences') {
      onClosePreferencesMode();
      return;
    }

    resetState();
  }, [openMode, onClosePreferencesMode, resetState]);

  const acceptAll = useCallback(() => {
    onDecision({
      statistics: true,
      marketing: true,
    });
  }, [onDecision]);

  const rejectNonEssential = useCallback(() => {
    onDecision({
      statistics: false,
      marketing: false,
    });
  }, [onDecision]);

  const savePreferences = useCallback(
    (preferences: CookiePreferences) => {
      onDecision({
        statistics: preferences.statistics,
        marketing: preferences.marketing,
      });
    },
    [onDecision]
  );

  const openPreferencesStep = useCallback(() => {
    setModalState(MODAL_STATE.preference);
  }, []);

  const initializeModalState = useCallback(() => {
    if (openMode === 'preferences') {
      setModalState(MODAL_STATE.preference);
      return;
    }

    resetState();
  }, [openMode, resetState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    initializeModalState();
  }, [isOpen, initializeModalState]);

  if (!isOpen || !appStore.singUpTestsReady || shouldWaitForAppInitialization) {
    return null;
  }

  const showNonBlocking =
    forcedNonBlocking ||
    appStore.activeSignUpAbTest('web_cookie_banner_v2') ||
    appStore.activeSignUpAbTest('web_cookie_banner_v3');

  if (modalState === MODAL_STATE.initial && showNonBlocking) {
    return (
      <CookieConsentInitialNonBlocking
        onCustomizeSettings={openPreferencesStep}
        onAcceptAll={acceptAll}
      />
    );
  }

  return (
    <BaseModal
      onClose={handleClose}
      disappearToBottom={false}
      withCloseButton={modalState === MODAL_STATE.preference}
      className="mx-4 max-h-[98dvh] w-full max-w-85.75 sm:max-w-166"
    >
      {modalState === MODAL_STATE.initial ? (
        <CookieConsentInitial
          onCustomizeSettings={openPreferencesStep}
          onAcceptAll={acceptAll}
        />
      ) : (
        <CookieConsentPreferences
          initialPreferences={initialPreferences}
          onAcceptAll={acceptAll}
          onRejectNonEssential={rejectNonEssential}
          onSavePreferences={savePreferences}
        />
      )}
    </BaseModal>
  );
};

export default observer(CookieConsentModal);
